import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { 
  Shield, 
  User, 
  CreditCard, 
  Trash2,
  Check,
  AlertCircle,
  Copy,
  QrCode,
  RefreshCw,
  Mail,
  Bell
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { api, user, refreshUser, logout } = useAuth();
  
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Profile
  const [name, setName] = useState(user?.name || '');
  const [nickname, setNickname] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Billing
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [pricing, setPricing] = useState(null);
  
  // Email preferences
  const [emailPrefs, setEmailPrefs] = useState({
    newsletter: true,
    watchlist_updates: true,
    price_alerts: true,
    weekly_summary: true
  });
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);
  
  // 2FA Setup
  const [setup2FA, setSetup2FA] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [enabling2FA, setEnabling2FA] = useState(false);
  
  // Disable 2FA
  const [disableCode, setDisableCode] = useState('');
  const [disabling2FA, setDisabling2FA] = useState(false);
  
  // Delete account
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleResetPortfolio = async () => {
    setResetting(true);
    try {
      const cashAmount = tierInfo?.tier === 'paid' ? selectedStartingCash : 10000;
      await api.post(`/settings/reset-portfolio?starting_cash=${cashAmount}`);
      toast.success(`Portfolio reset! You now have $${cashAmount.toLocaleString()} in virtual cash.`);
      refreshUser();
      // Refresh tier info to show updated starting cash
      const response = await api.get('/settings/tier');
      setTierInfo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset portfolio');
    } finally {
      setResetting(false);
    }
  };

  // Starting cash selection (Pro only)
  const [selectedStartingCash, setSelectedStartingCash] = useState(10000);
  const [startingCashOptions, setStartingCashOptions] = useState([10000]);

  useEffect(() => {
    const fetchStartingCashOptions = async () => {
      try {
        const response = await api.get('/settings/starting-cash-options');
        setStartingCashOptions(response.data.options);
        setSelectedStartingCash(response.data.current_starting_cash || 10000);
      } catch (error) {
        console.error('Failed to fetch starting cash options:', error);
      }
    };
    if (tierInfo?.tier === 'paid') {
      fetchStartingCashOptions();
    }
  }, [api, tierInfo?.tier]);

  // Upgrade tier
  const [upgrading, setUpgrading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleUpgradeTier = async () => {
    setUpgrading(true);
    try {
      await api.post('/settings/upgrade-tier');
      toast.success('Upgraded to Pro! Enjoy unlimited features.');
      const response = await api.get('/settings/tier');
      setTierInfo(response.data);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upgrade failed');
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.post('/settings/cancel-subscription');
      toast.success('Subscription cancelled. You are now on the Free plan.');
      const response = await api.get('/settings/tier');
      setTierInfo(response.data);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancellation failed');
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tierRes, profileRes, pricingRes, emailRes] = await Promise.all([
          api.get('/settings/tier'),
          api.get('/settings/profile'),
          api.get('/settings/pricing'),
          api.get('/settings/emails')
        ]);
        setTierInfo(tierRes.data);
        setNickname(profileRes.data.nickname || '');
        setBillingCycle(profileRes.data.billing_cycle || 'monthly');
        setPricing(pricingRes.data);
        setEmailPrefs(emailRes.data.preferences || {});
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Check if redirected from registration for 2FA setup
    if (location.state?.setup2fa && !user?.two_fa_enabled) {
      handleSetup2FA();
    }
  }, [api, location.state, user]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const params = new URLSearchParams();
      if (name) params.append('name', name);
      if (nickname) params.append('nickname', nickname);
      await api.put(`/settings/profile?${params.toString()}`);
      toast.success('Profile updated');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdateBillingCycle = async (cycle) => {
    try {
      await api.put(`/settings/billing-cycle?cycle=${cycle}`);
      setBillingCycle(cycle);
      toast.success(`Billing cycle updated to ${cycle}`);
    } catch (error) {
      toast.error('Failed to update billing cycle');
    }
  };

  const handleUpdateEmailPref = async (key, value) => {
    setSavingEmailPrefs(true);
    try {
      await api.put(`/settings/emails?${key}=${value}`);
      setEmailPrefs(prev => ({ ...prev, [key]: value }));
      toast.success('Email preferences updated');
    } catch (error) {
      toast.error('Failed to update email preferences');
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      const response = await api.post('/auth/2fa/setup');
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setSetup2FA(true);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    }
  };

  const handleEnable2FA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setEnabling2FA(true);
    try {
      await api.post('/auth/2fa/enable', { code: verifyCode });
      toast.success('2FA enabled successfully!');
      setSetup2FA(false);
      setVerifyCode('');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setEnabling2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setDisabling2FA(true);
    try {
      await api.post('/auth/2fa/disable', { code: disableCode });
      toast.success('2FA disabled');
      setDisableCode('');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/settings/account');
      toast.success('Account deleted');
      logout();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast.success('Secret copied to clipboard');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading settings...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1F2937]">Settings</h1>
          <p className="text-gray-500 mt-1">Manage your account and preferences</p>
        </div>

        {/* 2FA Setup Alert */}
        {!user?.two_fa_enabled && (
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-800">Enable Two-Factor Authentication</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Protect your account with 2FA. This is mandatory for full access to all features.
                  </p>
                  <Button
                    onClick={handleSetup2FA}
                    className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-white"
                    data-testid="setup-2fa-btn"
                  >
                    Set Up 2FA Now
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 2FA Setup Dialog */}
        {setup2FA && (
          <Card className="bg-white border-stone-100 shadow-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                Set Up Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl border border-stone-200">
                  <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              </div>
              
              {/* Manual Secret */}
              <div className="space-y-2">
                <Label className="text-gray-700">Or enter this code manually:</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={secret} 
                    readOnly 
                    className="font-mono text-sm bg-stone-50"
                  />
                  <Button variant="outline" size="icon" onClick={copySecret}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Verification */}
              <div className="space-y-2">
                <Label className="text-gray-700">Enter the 6-digit code from your app:</Label>
                <div className="flex justify-center">
                  <InputOTP 
                    maxLength={6} 
                    value={verifyCode} 
                    onChange={setVerifyCode}
                    data-testid="2fa-setup-code"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="w-12 h-14 text-xl border-stone-200" />
                      <InputOTPSlot index={1} className="w-12 h-14 text-xl border-stone-200" />
                      <InputOTPSlot index={2} className="w-12 h-14 text-xl border-stone-200" />
                      <InputOTPSlot index={3} className="w-12 h-14 text-xl border-stone-200" />
                      <InputOTPSlot index={4} className="w-12 h-14 text-xl border-stone-200" />
                      <InputOTPSlot index={5} className="w-12 h-14 text-xl border-stone-200" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSetup2FA(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEnable2FA}
                  disabled={enabling2FA || verifyCode.length !== 6}
                  className="flex-1 bg-[#1A4D2E] hover:bg-[#143d24]"
                  data-testid="enable-2fa-btn"
                >
                  {enabling2FA ? 'Verifying...' : 'Enable 2FA'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Section */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">Email</Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-stone-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nickname" className="text-gray-700">
                Nickname
                <span className="text-xs text-gray-400 ml-2">(Visible on leaderboard & shared portfolio)</span>
              </Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="e.g. TradingPro_123"
                maxLength={20}
                data-testid="profile-nickname-input"
              />
              <p className="text-xs text-gray-500">3-20 characters, letters, numbers, and underscores only</p>
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="bg-[#1A4D2E] hover:bg-[#143d24]"
              data-testid="save-profile-btn"
            >
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user?.two_fa_enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Shield className={`w-5 h-5 ${user?.two_fa_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-medium text-[#1F2937]">Two-Factor Authentication</p>
                  <p className="text-sm text-gray-500">
                    {user?.two_fa_enabled ? 'Enabled' : 'Not enabled'}
                  </p>
                </div>
              </div>
              {user?.two_fa_enabled ? (
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <Check className="w-4 h-4" />
                    Active
                  </span>
                </div>
              ) : (
                <Button
                  onClick={handleSetup2FA}
                  size="sm"
                  className="bg-[#1A4D2E] hover:bg-[#143d24]"
                >
                  Enable
                </Button>
              )}
            </div>
            
            {user?.two_fa_enabled && (
              <div className="space-y-3 pt-4 border-t border-stone-100">
                <Label className="text-gray-700">Disable 2FA (enter code to confirm)</Label>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    maxLength={6}
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit code"
                    className="max-w-[200px]"
                    data-testid="disable-2fa-code"
                  />
                  <Button
                    variant="outline"
                    onClick={handleDisable2FA}
                    disabled={disabling2FA || disableCode.length !== 6}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="disable-2fa-btn"
                  >
                    {disabling2FA ? 'Disabling...' : 'Disable 2FA'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Section */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-100">
              <div>
                <p className="font-semibold text-[#1A4D2E] capitalize">{tierInfo?.tier || 'Free'} Plan</p>
                <p className="text-sm text-green-700">
                  {tierInfo?.tier === 'paid' ? 'Full access to all features' : 'Upgrade for more features'}
                </p>
              </div>
              {tierInfo?.tier === 'free' ? (
                <Button 
                  onClick={handleUpgradeTier}
                  disabled={upgrading}
                  className="bg-[#1A4D2E] hover:bg-[#143d24] text-white"
                  data-testid="upgrade-tier-btn"
                >
                  {upgrading ? 'Upgrading...' : `Upgrade to Pro ($${billingCycle === 'yearly' ? '9.99' : '14.99'}/mo)`}
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-green-600 font-medium">✓ Pro Member</span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Subscription
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Pro Subscription?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You'll be downgraded to the Free plan with:
                          <ul className="mt-2 ml-4 list-disc text-sm">
                            <li>10 stocks in watchlist (vs 100)</li>
                            <li>20 trades per month (vs unlimited)</li>
                            <li>Limited price history</li>
                          </ul>
                          <p className="mt-2">You can upgrade again anytime.</p>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep Pro</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleCancelSubscription}
                          disabled={cancelling}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid="confirm-cancel-btn"
                        >
                          {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
            
            {/* Billing Cycle Toggle */}
            {pricing && (
              <div className="p-4 bg-stone-50 rounded-lg">
                <h4 className="font-medium text-[#1F2937] mb-3">Billing Cycle</h4>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleUpdateBillingCycle('monthly')}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      billingCycle === 'monthly' 
                        ? 'border-[#1A4D2E] bg-white' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid="billing-monthly"
                  >
                    <p className="font-semibold text-[#1F2937]">Monthly</p>
                    <p className="text-2xl font-bold text-[#1A4D2E]">${pricing.paid?.monthly}</p>
                    <p className="text-xs text-gray-500">per month</p>
                  </button>
                  <button
                    onClick={() => handleUpdateBillingCycle('yearly')}
                    className={`p-4 rounded-lg border-2 transition-colors relative ${
                      billingCycle === 'yearly' 
                        ? 'border-[#1A4D2E] bg-white' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid="billing-yearly"
                  >
                    <span className="absolute -top-2 -right-2 bg-[#C1F23D] text-[#1A4D2E] text-xs px-2 py-0.5 rounded-full font-medium">
                      Save ${pricing.paid?.monthly_savings}
                    </span>
                    <p className="font-semibold text-[#1F2937]">Yearly</p>
                    <p className="text-2xl font-bold text-[#1A4D2E]">${pricing.paid?.yearly}</p>
                    <p className="text-xs text-gray-500">per month, billed annually</p>
                  </button>
                </div>
              </div>
            )}
            
            <Separator />
            
            <div className="space-y-3">
              <h4 className="font-medium text-[#1F2937]">Usage</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-gray-500">Watchlist</p>
                  <p className="text-lg font-semibold text-[#1F2937]">
                    {tierInfo?.usage?.watchlist || 0} / {tierInfo?.limits?.watchlist || 10}
                  </p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-gray-500">Trades This Month</p>
                  <p className="text-lg font-semibold text-[#1F2937]">
                    {tierInfo?.usage?.trades_this_month || 0} / {tierInfo?.limits?.trades_per_month || 20}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Preferences Section */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Preferences
            </CardTitle>
            <CardDescription>
              Manage what emails you receive from FictoInvest
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-[#1F2937]">Newsletter</p>
                  <p className="text-sm text-gray-500">Weekly market insights and tips</p>
                </div>
                <Switch
                  checked={emailPrefs.newsletter}
                  onCheckedChange={(v) => handleUpdateEmailPref('newsletter', v)}
                  disabled={savingEmailPrefs}
                  data-testid="email-newsletter"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-[#1F2937]">Watchlist Updates</p>
                  <p className="text-sm text-gray-500">Daily summary of your watchlist stocks</p>
                </div>
                <Switch
                  checked={emailPrefs.watchlist_updates}
                  onCheckedChange={(v) => handleUpdateEmailPref('watchlist_updates', v)}
                  disabled={savingEmailPrefs}
                  data-testid="email-watchlist"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-[#1F2937]">Price Alerts</p>
                  <p className="text-sm text-gray-500">Notifications when price targets are hit</p>
                  {tierInfo?.tier === 'free' && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded mt-1 inline-block">Pro only</span>
                  )}
                </div>
                <Switch
                  checked={emailPrefs.price_alerts}
                  onCheckedChange={(v) => handleUpdateEmailPref('price_alerts', v)}
                  disabled={savingEmailPrefs || tierInfo?.tier === 'free'}
                  data-testid="email-alerts"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-[#1F2937]">Weekly Portfolio Summary</p>
                  <p className="text-sm text-gray-500">Weekly recap of your portfolio performance</p>
                </div>
                <Switch
                  checked={emailPrefs.weekly_summary}
                  onCheckedChange={(v) => handleUpdateEmailPref('weekly_summary', v)}
                  disabled={savingEmailPrefs}
                  data-testid="email-summary"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-white border-red-100 shadow-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-red-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reset Portfolio */}
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-100">
              <div className="flex-1">
                <p className="font-medium text-yellow-800">Reset Portfolio</p>
                <p className="text-sm text-yellow-600">
                  Start fresh. Removes all positions & trades, keeps your watchlist.
                </p>
                {tierInfo?.tier === 'paid' && (
                  <div className="mt-3">
                    <p className="text-xs text-yellow-700 mb-2">Pro: Choose your starting cash</p>
                    <div className="flex flex-wrap gap-2">
                      {startingCashOptions.map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setSelectedStartingCash(amount)}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            selectedStartingCash === amount
                              ? 'bg-[#1A4D2E] text-white'
                              : 'bg-white text-yellow-800 border border-yellow-300 hover:border-yellow-500'
                          }`}
                        >
                          ${amount.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-yellow-300 text-yellow-700 hover:bg-yellow-100 ml-4" data-testid="reset-portfolio-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset Your Portfolio?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will:
                      <ul className="mt-2 ml-4 list-disc text-sm">
                        <li>Reset your cash balance to ${(tierInfo?.tier === 'paid' ? selectedStartingCash : 10000).toLocaleString()}</li>
                        <li>Remove all your stock positions</li>
                        <li>Clear your transaction history</li>
                        <li><strong>Keep your watchlist intact</strong></li>
                      </ul>
                      <p className="mt-2 font-medium">This action cannot be undone.</p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleResetPortfolio}
                      disabled={resetting}
                      className="bg-yellow-600 hover:bg-yellow-700"
                      data-testid="confirm-reset-btn"
                    >
                      {resetting ? 'Resetting...' : 'Yes, Reset Portfolio'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {/* Delete Account */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
              <div>
                <p className="font-medium text-red-800">Delete Account</p>
                <p className="text-sm text-red-600">
                  Permanently delete your account and all data
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" data-testid="delete-account-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your account,
                      portfolio, watchlist, and all transaction history.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? 'Deleting...' : 'Yes, delete my account'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default SettingsPage;
